/* eslint-disable react/prop-types */
// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonBadge from "components/ArgonBadge";
import ArgonTypography from "components/ArgonTypography";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { Tooltip } from "@mui/material";

const formatDateTime = (value) => {
  if (!value) {
    return "N/A";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "N/A";
  }

  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (value) => {
  if (!value) {
    return "N/A";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "N/A";
  }

  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (value) => {
  if (!value) {
    return "N/A";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "N/A";
  }

  return parsed.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const renderAttachment = (url) => {
  if (!url) {
    return (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        Not uploaded
      </ArgonTypography>
    );
  }

  return (
    <ArgonButton
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      variant="outlined"
      color="info"
      size="small"
      sx={{ fontSize: "12px", py: 0.5, px: 1.5 }}
    >
      View
    </ArgonButton>
  );
};

const getTutor = (attendance) => {
  if (attendance.tutor) {
    return attendance.tutor;
  }

  const [sessionTutor] = attendance.classSession?.tutors || [];
  return sessionTutor || null;
};

const getStudentsLabel = (attendance) => {
  const students = attendance.classSession?.students || [];
  if (!students.length) {
    return "N/A";
  }

  if (students.length === 1) {
    return students[0].name || "N/A";
  }

  return `${students.length} Students`;
};

const getStatusColor = (status) => {
  switch (status) {
    case "INCOMPLETE":
      return "error";
    case "COMPLETED":
      return "success";
    case "ONGOING":
      return "info";
    case "SCHEDULED":
    case "CREATED":
      return "primary";
    case "ON_HOLD":
      return "warning";
    case "CANCELLED":
      return "error";
    default:
      return "secondary";
  }
};

const AttendanceListViewTutorSummaryTableData = (
  attendanceList = [],
  handleEditClick,
  handleDeleteClick = null
) => {
  const columns = [
    { name: "sno", align: "center", width: "5%" },
    { name: "Date", align: "left", width: "15%" },
    { name: "Start", align: "center", width: "8%" },
    { name: "End", align: "center", width: "8%" },
    { name: "Class Type", align: "left", width: "18%" },
    { name: "Tutor", align: "left", width: "15%" },
    { name: "Students", align: "left", width: "12%" },
    { name: "Class Status", align: "center", width: "10%" },
    { name: "Leave By", align: "center", width: "8%" },
    { name: "Early Checkout Reason", align: "center", width: "8%" },
    { name: "Home Work", align: "center", width: "8%" },
    { name: "Home Note", align: "left", width: "18%" },
    { name: "Voice Note", align: "center", width: "8%" },
    { name: "Action", align: "center", width: "5%" },
  ];

  const rows = attendanceList.map((attendance, index) => {
    const tutor = getTutor(attendance);

    // Debug logging
    console.log(`Attendance ${index + 1}:`, {
      id: attendance.id,
      hasEndTime: !!attendance.endTime,
      endTime: attendance.endTime,
      hasStartTime: !!attendance.startTime,
      startTime: attendance.startTime,
      topicCovered: attendance.topicCovered,
      type: attendance.type,
    });

    // Get the attendance type - it can be an array or string
    const attendanceType = Array.isArray(attendance.type)
      ? attendance.type
      : attendance.type
      ? [attendance.type]
      : [];

    // Check if type includes "ABSENT"
    const isAbsent = attendanceType.some((t) => String(t).toUpperCase() === "ABSENT");
    const hasEarlyAttendanceNote =
      typeof attendance.earlyAttendanceNote === "string" &&
      attendance.earlyAttendanceNote.trim() !== "";
    const isIncomplete = attendance.earlyAttendance === true || hasEarlyAttendanceNote;

    // Determine class status based on attendance state
    let classStatus = "CREATED";

    if (isIncomplete) {
      classStatus = "INCOMPLETE";
    } else if (isAbsent) {
      // If marked as absent, status is ON_HOLD
      classStatus = "ON_HOLD";
    } else if (attendance.endTime) {
      // If endTime exists, attendance is completed (most reliable check)
      classStatus = "COMPLETED";
    } else if (attendance.topicCovered && attendance.topicCovered.trim() !== "") {
      // If topic covered is filled, attendance is completed
      classStatus = "COMPLETED";
    } else if (attendance.startTime && !attendance.endTime) {
      // If class has started but not ended, it's ongoing
      classStatus = "ONGOING";
    } else if (attendance.attendanceTime && new Date(attendance.attendanceTime) > new Date()) {
      // If attendance time is in future, it's scheduled
      classStatus = "SCHEDULED";
    } else {
      // Default fallback
      classStatus = "CREATED";
    }

    console.log(`Attendance ${index + 1} final status:`, classStatus);

    // Check if the class is upcoming (attendance time is in the future)
    const isUpcoming =
      attendance.attendanceTime && new Date(attendance.attendanceTime) > new Date();
    // Only disable for upcoming (future) classes
    const disableAction = isUpcoming;

    return {
      sno: (
        <ArgonTypography
          variant="caption"
          color={disableAction ? "secondary" : "text"}
          fontWeight="medium"
        >
          {index + 1}
        </ArgonTypography>
      ),
      Date: (
        <ArgonTypography
          variant="caption"
          color={disableAction ? "secondary" : "text"}
          fontWeight="medium"
        >
          {formatDate(attendance.attendanceTime)}
        </ArgonTypography>
      ),
      Start: (
        <ArgonTypography
          variant="caption"
          color={disableAction ? "secondary" : "text"}
          fontWeight="medium"
        >
          {attendance.startTime ? formatTime(attendance.startTime) : "N/A"}
        </ArgonTypography>
      ),
      End: (
        <ArgonTypography
          variant="caption"
          color={disableAction ? "secondary" : "text"}
          fontWeight="medium"
        >
          {attendance.endTime ? formatTime(attendance.endTime) : "N/A"}
        </ArgonTypography>
      ),
      "Class Type": (
        <ArgonTypography
          variant="caption"
          color={disableAction ? "secondary" : "text"}
          fontWeight="medium"
        >
          {attendance.type || "N/A"}
        </ArgonTypography>
      ),
      Tutor: (
        <ArgonBox display="flex" flexDirection="column">
          <ArgonTypography
            variant="caption"
            fontWeight="medium"
            color={disableAction ? "secondary" : "text"}
          >
            {tutor?.name || tutor?.fullName || "N/A"}
          </ArgonTypography>
          {(tutor?.email || tutor?.phone) && (
            <ArgonTypography variant="caption" color="secondary">
              {tutor?.email || tutor?.phone}
            </ArgonTypography>
          )}
        </ArgonBox>
      ),
      Students: (
        <ArgonTypography
          variant="caption"
          color={disableAction ? "secondary" : "text"}
          fontWeight="medium"
        >
          {getStudentsLabel(attendance)}
        </ArgonTypography>
      ),
      "Class Status": (
        <ArgonBadge
          variant="gradient"
          badgeContent={classStatus}
          color={getStatusColor(classStatus)}
          sizeFor="xs"
          container
        />
      ),
      "Leave By": (
        <ArgonTypography
          variant="caption"
          color={disableAction ? "secondary" : "text"}
          fontWeight="medium"
        >
          {attendance.leaveFor
            ? `ABSENT BY ${String(attendance.leaveFor)}`
            : "N/A"}
        </ArgonTypography>
      ),
      "Early Checkout Reason": (() => {
        const fullText = attendance.earlyAttendanceNote || "N/A";

        // Extract short message (text before parenthesis)
        const shortMessage = fullText.includes("(") ? fullText.split("(")[0].trim() : fullText;

        // If there's no additional info, just show the text
        if (fullText === "N/A" || !fullText.includes("(")) {
          return (
            <ArgonTypography
              variant="caption"
              color={disableAction ? "secondary" : "text"}
              fontWeight="medium"
            >
              {fullText}
            </ArgonTypography>
          );
        }

        // Show short message with full details on hover
        return (
          <Tooltip title={fullText} placement="top" arrow>
            <ArgonTypography
              variant="caption"
              color={disableAction ? "secondary" : "text"}
              fontWeight="medium"
              sx={{ cursor: "pointer" }}
            >
              {shortMessage}...
            </ArgonTypography>
          </Tooltip>
        );
      })(),
      "Home Work": renderAttachment(attendance.homeWork),
      "Home Note": (
        <ArgonTypography
          variant="caption"
          color={disableAction ? "secondary" : "text"}
          fontWeight="medium"
        >
          {attendance.topicCovered || "N/A"}
        </ArgonTypography>
      ),
      "Voice Note": renderAttachment(attendance.voiceNote),
      Action: (
        <ArgonBox display="flex" gap={1} justifyContent="center">
          {/* Edit: always enabled for all classes */}
          <Tooltip title="Edit" placement="top">
            <span>
              <ArgonButton
                variant="text"
                size="small"
                onClick={() => handleEditClick && handleEditClick(attendance)}
                sx={{ minWidth: "32px", p: 0 }}
              >
                <EditIcon color="info" fontSize="small" />
              </ArgonButton>
            </span>
          </Tooltip>
          {/* Delete: always enabled for all classes */}
          <Tooltip title="Delete" placement="top">
            <span>
              <ArgonButton
                variant="text"
                size="small"
                onClick={() => handleDeleteClick && handleDeleteClick(attendance)}
                sx={{ minWidth: "32px", p: 0 }}
              >
                <DeleteIcon color="error" fontSize="small" />
              </ArgonButton>
            </span>
          </Tooltip>
        </ArgonBox>
      ),
    };
  });

  return { columns, rows };
};

export default AttendanceListViewTutorSummaryTableData;
